import { zodResolver } from '@hookform/resolvers/zod';
import { useSession } from '@hooks/security/use-session';
import { useAssessOrganDisease } from '@hooks/register/screening/use-assess-organ-disease';
import { useLocalize } from '@hooks/use-localize';
import { useStepForm } from '@hooks/use-step-form';
import { assessOrganDiseaseSchema } from '@views/register/screening/assess-organ-disease.schema';
import { asStringBool } from '@/utils/parse';
import { DashRegistrationStep, screeningLinkMap } from '@/types/dashboard';
import type { IAssessOrganDiseaseFormValues, IAssessOrganDiseaseModel, IAssessOrganDiseaseSubmitResult } from '@/types/screening/assess-organ-disease';
import PageLoader from '@components/page-loader';
import StepFormLayout from '@components/step-form-layout';
import FieldError from '@components/field-error';

const AssessOrganDisease = () => {
    const { registrationId, editKey } = useSession();
    const { model, isLoading, error, submit, isSubmitting, submitError } = useAssessOrganDisease(registrationId, editKey);

    return (
        <PageLoader isLoading={isLoading} error={error}>
            <AssessOrganDiseaseContent
                model={model}
                isSubmitting={isSubmitting}
                submitError={submitError}
                onSubmit={submit}
            />
        </PageLoader>
    );
};

interface IAssessOrganDiseaseContentProps {
    model: IAssessOrganDiseaseModel;
    isSubmitting: boolean;
    submitError: string | null;
    onSubmit: (values: IAssessOrganDiseaseFormValues) => Promise<IAssessOrganDiseaseSubmitResult>;
}

const AssessOrganDiseaseContent = ({ model, isSubmitting, submitError, onSubmit }: IAssessOrganDiseaseContentProps) => {
    const t = useLocalize(model.languageData);

    const form = useStepForm<IAssessOrganDiseaseFormValues, IAssessOrganDiseaseSubmitResult>({
        resolver: zodResolver(assessOrganDiseaseSchema),
        defaultValues: {
            hasHadOrganDisease: asStringBool(model.hasHadOrganDisease),
        },
        submit: onSubmit,
        linkMap: screeningLinkMap(model.isFemale, model.isLiver),
    });

    const {
        register,
        formState: { errors },
    } = form;

    return (
        <StepFormLayout
            kind='screening'
            model={model}
            step={DashRegistrationStep.AssessOrganDisease}
            title={t('DashLabel_ProgressKidneyDisease', 'Kidney Disease')}
            t={t}
            form={form}
            isSubmitting={isSubmitting}
            submitError={submitError}
        >
            <div className='form-group'>
                <label className='control-label col-xs-12'>
                    {t(
                        'DashLabel_HaveYouEverBeenDiagnosedWithAnyOfTheFollowingKidney',
                        'Have you ever been diagnosed with any of the following kidney diseases',
                    )}
                    ?<FieldError message={errors.hasHadOrganDisease?.message} />
                </label>

                <div className='col-xs-12'>
                    <ul className='col-xs-12' style={{ marginLeft: 10 }}>
                        <li>
                            {t(
                                'DashLabel_PolycysticKidneyDiseasePkd',
                                'Polycystic kidney disease (PKD)',
                            )}
                        </li>
                        <li>{t('DashLabel_IgaNephropathy', 'IgA nephropathy')}</li>
                        <li>{t('DashLabel_LupusNephritis', 'Lupus nephritis')}</li>
                        <li>{t('Word_Glomerulonephritis', 'Glomerulonephritis')}</li>
                    </ul>
                </div>

                <div className='col-xs-12' style={{ marginLeft: 10 }}>
                    <label className='radio-inline'>
                        <input
                            type='radio'
                            value='1'
                            {...register('hasHadOrganDisease')}
                        />{' '}
                        {t('Word_Yes', 'Yes')}
                    </label>
                    <label className='radio-inline'>
                        <input
                            type='radio'
                            value='0'
                            {...register('hasHadOrganDisease')}
                        />{' '}
                        {t('Word_No', 'No')}
                    </label>
                </div>
            </div>
        </StepFormLayout>
    );
};

export default AssessOrganDisease;
